import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, TrendingUp, Clock, Briefcase, FileText,
  Users, Settings, LogOut, UserCircle, UserSquare2, ScrollText
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/performance", label: "Performance", icon: TrendingUp, testid: "nav-performance" },
  { to: "/timesheet", label: "Timesheet", icon: Clock, testid: "nav-timesheet" },
  { to: "/projects", label: "Project", icon: Briefcase, testid: "nav-projects" },
  { to: "/templates", label: "Template Activity", icon: FileText, testid: "nav-templates" },
  { to: "/clients", label: "Client", icon: UserCircle, testid: "nav-clients" },
  { to: "/product-managers", label: "Product Manager", icon: UserSquare2, testid: "nav-product-managers" },
];

export default function Layout() {
  const { user, logout, isLeaderView } = useAuth();
  const nav = useNavigate();
  const isLeader = isLeaderView;
  const isManager = user?.role === "manager";

  // Manager only observes: hide Performance & Timesheet (they get team stats via Dashboard)
  const baseNav = isManager
    ? NAV.filter((n) => !["/performance", "/timesheet"].includes(n.to))
    : NAV;

  const items = [
    ...baseNav,
    ...(isLeader ? [
      { to: "/users", label: "User Management", icon: Users, testid: "nav-users" },
      { to: "/audit", label: "Audit Trail", icon: ScrollText, testid: "nav-audit" },
    ] : []),
    { to: "/settings", label: "Setting", icon: Settings, testid: "nav-settings" },
  ];

  const doLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 shrink-0 border-r border-border flex-col bg-card">
        <div className="h-16 px-5 flex items-center border-b border-border">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-sm bg-foreground text-background flex items-center justify-center font-black">P</div>
            <div>
              <div className="font-heading font-black text-sm tracking-tight leading-none">Presales</div>
              <div className="label-mini leading-none mt-0.5">Collab</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {items.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <div className="label-mini">Signed in as</div>
            <div className="text-sm font-semibold truncate mt-0.5">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            <div className="mt-1 inline-flex items-center text-[10px] tracking-widest uppercase font-bold px-2 py-0.5 rounded-sm bg-primary/10 text-primary">
              {user?.role === "team_leader" ? "Team Leader" : user?.role === "manager" ? "Manager" : "Member"}
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start rounded-sm"
            onClick={doLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" /> Keluar
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-20 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="md:hidden h-8 w-8 rounded-sm bg-foreground text-background flex items-center justify-center font-black">P</div>
            <div className="label-mini hidden sm:block">Enterprise · Presales Ops</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-xs text-muted-foreground">Domain</div>
              <div className="text-xs font-semibold">{user?.domain || "presales"}</div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="p-6 md:p-8 max-w-[1600px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
